/* eslint-disable react/require-default-props */
import { forwardRef } from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';

const buttonVariants = cva('btn', {
  variants: {
    variant: {
      default: 'btn-primary',
      outline: 'btn-outline',
      teriary: 'btn-tertiary',
      brand: 'btn-brand',
      brandOutline: 'btn-outline-brand',
      danger: 'btn-danger',
    },
    size: {
      default: '',
      sm: 'btn-sm',
      lg: 'btn-lg',
    },
    radius: {
      default: '',
      none: 'rounded-0',
      sm: 'rounded',
      md: 'rounded-md',
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'default',
    radius: 'default',
  },
});

export type ButtonVariantsType =
  | 'default'
  | 'brand'
  | 'brandOutline'
  | 'danger';

export type ButtonProps = {
  asChild?: boolean;
  block?: boolean;
} & React.ButtonHTMLAttributes<HTMLButtonElement> &
  VariantProps<typeof buttonVariants>;

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      radius,
      asChild = false,
      block = false,
      ...props
    },
    ref,
  ) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        className={cn(
          buttonVariants({ variant, size, radius }),
          block && 'btn-block',
          className,
        )}
        ref={ref}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { Button, buttonVariants };
